import type {
  CartItemInputDto,
  CartItemOutputDto,
  ItemAdhesive,
  ItemFinish,
  ItemLaminate,
  ItemMaterial,
  ItemOrientation,
  ItemOutputDto,
  ItemPriceResultDto,
  ItemShape,
  ProductPublicDto,
  SaveItemRequest,
  SpecVersionOutputPublicDto,
} from "@sticker-giant/sg-frontend-common";
import { ThumbnailGenerator } from "@sticker-giant/sg-frontend-common";
import { getItemPrice } from "@/lib/product/pricing";
import { resolveMaterialScopedFields, soleOptionSku } from "@/lib/product/validation";
import { useAuthClaimsStore } from "@/stores/useAuthClaimsStore";
import { usePdpStore } from "@/stores/usePdpStore";
import { GuestIdHelper } from "@/utils/GuestIdHelper";
import { PackagingMode, resolvePackagingMode } from "@/utils/resolvePackagingMode";
import { getRollDriver } from "@/utils/rollQuantityUtils";

function buildSkuFromPdpStore(productAdmin: ProductPublicDto) {
  const state = usePdpStore.getState();
  const materialSku = state.material ?? productAdmin.defaultMaterial;
  const { adhesiveDefault, laminateDefault } = resolveMaterialScopedFields(productAdmin, materialSku);

  return {
    marketingName: productAdmin.sku,
    product: productAdmin.pricingProductType.sku,
    material: materialSku ?? soleOptionSku(productAdmin.materialOptions),
    shape: state.shape ?? productAdmin.defaultShape ?? soleOptionSku(productAdmin.shapeOptions),
    finish: state.finish ?? productAdmin.defaultFinishing ?? soleOptionSku(productAdmin.finishingOptions),
    laminate: state.laminate ?? laminateDefault,
    adhesive: state.adhesive ?? adhesiveDefault,
    orientation: state.orientation ?? productAdmin.defaultOrientation ?? soleOptionSku(productAdmin.orientationOptions),
    addons: [],
  };
}

export function getCustomerInfo() {
  const sgCustomerId = useAuthClaimsStore.getState().claims?.sg_customer_id;
  return {
    customerId: sgCustomerId ?? GuestIdHelper.getOrCreateGuestId(),
    isLoggedIn: !!sgCustomerId,
  };
}

interface BuildPdpCartItemParams {
  productAdmin: ProductPublicDto;
  pricingData?: ItemPriceResultDto;
  customPricingData?: ItemPriceResultDto;
  itemId?: string;
}

export function buildPdpCartItem({
  productAdmin,
  pricingData,
  customPricingData,
  itemId,
}: BuildPdpCartItemParams): CartItemInputDto {
  const state = usePdpStore.getState();
  if (!state.quantity) throw new Error("Quantity is required to build a cart item");
  const quantity = state.quantity;
  const sku = buildSkuFromPdpStore(productAdmin);
  // Stack/bundle packaging is not offered yet, so stale values hydrated from the cart must not be resent
  const isRoll = resolvePackagingMode(sku.finish) === PackagingMode.Roll;

  return {
    ...(itemId && { id: itemId }),
    isReorder: false,
    noArt: !state.uploadedFileInfo?.filepath,
    fileUrl: state.uploadedFileInfo?.filepath,
    quantity,
    width: state.width ?? null,
    height: state.height ?? null,
    imageWidth: state.imageWidth,
    imageHeight: state.imageHeight,
    itemPrice: getItemPrice(quantity, pricingData, customPricingData),
    itemNotes: state.specialInstructions ?? "",
    sku,
    customer: getCustomerInfo(),
    rollCount: isRoll ? state.rollCount : null,
    rollQty: isRoll ? state.rollQty : null,
    rollDriver: isRoll ? state.rollDriver : null,
  };
}

interface BuildReorderCartItemParams {
  item: ItemOutputDto;
  spec: SpecVersionOutputPublicDto;
  cartId?: string;
}

export function buildReorderCartItem({ item, spec, cartId }: BuildReorderCartItemParams): CartItemInputDto {
  const artworkFile = ThumbnailGenerator.getArtwork(spec);

  return {
    artworkPage: artworkFile.pageNumber,
    ...(cartId && { cartId }),
    description: item.description,
    specId: item.specId,
    isReorder: true,
    noArt: !artworkFile.fileName,
    fileUrl: artworkFile.fileName,
    quantity: item.quantity,
    width: spec.specProperties.width,
    height: spec.specProperties.height,
    sku: {
      marketingName: item.sku.marketingName,
      product: item.sku.productType,
      material: spec.sku.material as ItemMaterial,
      shape: spec.sku.shape as ItemShape,
      finish: item.sku.itemFinish as ItemFinish,
      laminate: item.sku.laminate as ItemLaminate,
      adhesive: item.sku.itemAdhesive as ItemAdhesive,
      orientation: item.sku.rewindPosition as ItemOrientation,
      addons: [],
    },
    customer: getCustomerInfo(),
    rollCount: item.rollCount,
    rollQty: item.rollQty,
    rollDriver: getRollDriver(item),
  };
}

export function hydrateStoreFromCartItem(cartItem: CartItemOutputDto) {
  const store = usePdpStore.getState();

  store.setMaterial(cartItem.sku.material);
  store.setShape(cartItem.sku.shape);
  store.setFinish(cartItem.sku.finish);
  store.setLaminate(cartItem.sku.laminate);
  store.setAdhesive(cartItem.sku.adhesive);
  store.setOrientation(cartItem.sku.orientation);
  store.setQuantity(cartItem.quantity);
  store.setRollQty(cartItem.rollQty);
  store.setRollCount(cartItem.rollCount);
  store.setRollDriver(getRollDriver(cartItem));
  store.setWidth(cartItem.width);
  store.setHeight(cartItem.height);
  store.setSpecialInstructions(cartItem.itemNotes);

  if (!cartItem.noArt) {
    store.setUploadedFileInfo({
      filepath: cartItem.fileUrl,
      bucket: cartItem.fileBucket,
      filename: cartItem.fileUrl,
      url: "",
    });
  }
}

interface BuildSaveForLaterParams {
  productAdmin: ProductPublicDto;
  email?: string;
  specId?: string;
}

export function buildSaveForLaterRequest({ productAdmin, email, specId }: BuildSaveForLaterParams): SaveItemRequest {
  const pdpState = usePdpStore.getState();
  const customerId = useAuthClaimsStore.getState().claims?.sg_customer_id;

  return {
    customer: {
      customerId: customerId ?? null,
      email: email || null,
    },
    sku: buildSkuFromPdpStore(productAdmin),
    itemNotes: pdpState.specialInstructions,
    height: pdpState.height,
    width: pdpState.width,
    fileUrl: pdpState.uploadedFileInfo?.filepath,
    specId,
  };
}

export function hydrateStoreFromSpec(spec: SpecVersionOutputPublicDto) {
  const store = usePdpStore.getState();

  store.setMaterial(spec.sku.material || undefined);
  store.setShape(spec.sku.shape || undefined);
  store.setFinish(spec.sku.finish || undefined);
  store.setLaminate(spec.sku.laminate || undefined);
  store.setAdhesive(spec.sku.adhesive || undefined);
  store.setOrientation(spec.sku.orientation || undefined);
  store.setHeight(spec.specProperties.height ?? undefined);
  store.setWidth(spec.specProperties.width ?? undefined);
  store.setSpecialInstructions(spec.customerNotes || undefined);

  if (spec.originalArtFile) {
    store.setUploadedFileInfo({
      filepath: spec.originalArtFile.file.filePath,
      bucket: spec.originalArtFile.file.volume.config.s3_bucket,
      filename: spec.originalArtFile.file.fileName,
      url: "",
    });
  }
}
